"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";

import { cn } from "~/lib/utils";
import type { PlpView } from "~/lib/storefront/plp-params";
import { useTranslator } from "~/i18n/provider";

/**
 * P2-09 · Grid/list view toggle. Writes `view=` to the URL (default grid is
 * dropped to keep canonical URLs clean) and refetches via App-Router
 * navigation, preserving all other params. Rendered only when the brand
 * enables list view.
 */
export function ViewToggle({ view }: { view: PlpView }) {
  const t = useTranslator();
  const router = useRouter();
  const pathname = usePathname();
  const params = useSearchParams();

  const select = (next: PlpView) => {
    if (next === view) return;
    const sp = new URLSearchParams(params);
    if (next === "grid") sp.delete("view");
    else sp.set("view", next);
    sp.delete("page"); // a layout change returns to the first page
    const qs = sp.toString();
    router.push(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
  };

  return (
    <div
      role="group"
      aria-label={t("browse.view")}
      className="inline-flex overflow-hidden rounded-md border border-border"
    >
      {(["grid", "list"] as const).map((v) => (
        <button
          key={v}
          type="button"
          aria-pressed={view === v}
          aria-label={`${v} view`}
          onClick={() => select(v)}
          className={cn(
            "flex h-9 w-9 items-center justify-center text-fg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]",
            view === v ? "bg-surface-2 text-fg" : "hover:bg-surface-2",
          )}
        >
          {v === "grid" ? <GridIcon /> : <ListIcon />}
        </button>
      ))}
    </div>
  );
}

function GridIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      className="h-4 w-4"
    >
      <rect x="3.5" y="3.5" width="7" height="7" rx="1" />
      <rect x="13.5" y="3.5" width="7" height="7" rx="1" />
      <rect x="3.5" y="13.5" width="7" height="7" rx="1" />
      <rect x="13.5" y="13.5" width="7" height="7" rx="1" />
    </svg>
  );
}

function ListIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      className="h-4 w-4"
    >
      <path d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  );
}
